/** API client for attachment file management. */

import { apiGet, apiPost, apiDelete } from '@/api/client';

interface RequestUploadUrlResponse {
  attachmentId: string;
  s3Key: string;
  uploadUrl: string;
}

interface GetDownloadUrlResponse {
  downloadUrl: string;
}

/**
 * Request a pre-signed S3 upload URL for a new attachment.
 */
export async function requestUploadUrl(
  mapId: string,
  fileName: string,
  mimeType: string,
  sizeBytes: number,
): Promise<{ attachmentId: string; s3Key: string; uploadUrl: string }> {
  return apiPost<RequestUploadUrlResponse>(`/maps/${mapId}/attachments`, {
    fileName,
    mimeType,
    sizeBytes,
  });
}

/**
 * Get a pre-signed S3 download URL for an existing attachment.
 */
export async function getDownloadUrl(
  mapId: string,
  attachmentId: string,
  s3Key: string,
): Promise<string> {
  const encodedKey = encodeURIComponent(s3Key);
  const resp = await apiGet<GetDownloadUrlResponse>(
    `/maps/${mapId}/attachments/${attachmentId}?s3Key=${encodedKey}`,
  );
  return resp.downloadUrl;
}

/**
 * Delete an attachment from S3.
 */
export async function deleteAttachment(
  mapId: string,
  attachmentId: string,
  s3Key: string,
): Promise<void> {
  const encodedKey = encodeURIComponent(s3Key);
  await apiDelete(`/maps/${mapId}/attachments/${attachmentId}?s3Key=${encodedKey}`);
}

/**
 * Upload a file directly to S3 using the pre-signed PUT URL.
 */
export async function uploadFile(uploadUrl: string, file: File): Promise<void> {
  const resp = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
    },
    body: file,
  });

  if (!resp.ok) {
    throw new Error(`Upload failed: ${resp.status} ${resp.statusText}`);
  }
}
